import { useEffect, useCallback } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Img } from "./Img";

/* Full-screen editorial lightbox gallery for portfolio images. */
export const Lightbox = ({ items, index, onClose, onNavigate }) => {
  const reduce = useReducedMotion();
  const open = index !== null && index >= 0;

  const go = useCallback(
    (dir) => {
      if (!open) return;
      const next = (index + dir + items.length) % items.length;
      onNavigate(next);
    },
    [open, index, items, onNavigate]
  );

  useEffect(() => {
    if (!open) return;
    // lock background scroll + pause smooth scroll
    const lenis = window.__lenis;
    if (lenis) lenis.stop();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (lenis) lenis.start();
    };
  }, [open, onClose, go]);

  const item = open ? items[index] : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lightbox"
          className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-sm flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={item?.alt || "Portfolio image"}
        >
          {/* top bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 md:px-10 h-16 md:h-20">
            <span className="kicker !text-beige/70">
              {item?.category} · {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
            </span>
            <button
              data-testid="lightbox-close"
              onClick={onClose}
              className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-beige/30 text-beige hover:bg-beige hover:text-ink transition-colors"
              aria-label="Close gallery"
            >
              <X size={20} />
            </button>
          </div>

          {/* prev / next */}
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-3 md:left-8 z-10 inline-flex items-center justify-center w-12 h-12 rounded-full border border-beige/30 text-beige hover:bg-beige hover:text-ink transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-3 md:right-8 z-10 inline-flex items-center justify-center w-12 h-12 rounded-full border border-beige/30 text-beige hover:bg-beige hover:text-ink transition-colors"
            aria-label="Next image"
          >
            <ChevronRight size={22} />
          </button>

          {/* image */}
          <AnimatePresence mode="wait">
            <motion.figure
              key={item?.img}
              className="relative max-w-[90vw] md:max-w-[70vw] max-h-[78vh] px-4"
              initial={reduce ? { opacity: 1 } : { opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reduce ? { opacity: 1 } : { opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <Img
                name={item.img}
                alt={item.alt}
                fit="contain"
                loading="eager"
                imgClassName="max-h-[78vh] w-auto mx-auto rounded-sm"
              />
              <figcaption className="mt-4 text-center kicker !text-beige/60">
                {item?.alt}
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
